import { useEffect, useRef } from 'react';
const DRAG_SENS = 0.0007; // was 0.003

export default function GeometricBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = canvas.offsetWidth;
    const H = canvas.offsetHeight;
    const DPR = window.devicePixelRatio || 1;
    canvas.width = W * DPR;
    canvas.height = H * DPR;
    const ctx = canvas.getContext('2d');
    ctx.scale(DPR, DPR);

    // --- Math helpers ---
    function rotateX(p, a) {
      const cos = Math.cos(a), sin = Math.sin(a);
      return [p[0], p[1] * cos - p[2] * sin, p[1] * sin + p[2] * cos];
    }
    function rotateY(p, a) {
      const cos = Math.cos(a), sin = Math.sin(a);
      return [p[0] * cos + p[2] * sin, p[1], -p[0] * sin + p[2] * cos];
    }
    function project(p, fov, cx, cy) {
      const z = p[2] + fov;
      const s = fov / z;
      return [p[0] * s + cx, p[1] * s + cy, z];
    }

    // --- Geometry builders ---
    function makeCube(size) {
      const h = size / 2;
      const verts = [
        [-h,-h,-h],[h,-h,-h],[h,h,-h],[-h,h,-h],
        [-h,-h, h],[h,-h, h],[h,h, h],[-h,h, h],
      ];
      const edges = [
        [0,1],[1,2],[2,3],[3,0],
        [4,5],[5,6],[6,7],[7,4],
        [0,4],[1,5],[2,6],[3,7],
      ];
      return { verts, edges };
    }

    function makeOctahedron(size) {
      const s = size;
      const verts = [[0,s,0],[0,-s,0],[s,0,0],[-s,0,0],[0,0,s],[0,0,-s]];
      const edges = [
        [0,2],[0,3],[0,4],[0,5],
        [1,2],[1,3],[1,4],[1,5],
        [2,4],[4,3],[3,5],[5,2],
      ];
      return { verts, edges };
    }

    function makeIcosahedron(size) {
      const t = (1 + Math.sqrt(5)) / 2;
      const s = size / Math.sqrt(1 + t * t);
      const raw = [
        [-1,t,0],[1,t,0],[-1,-t,0],[1,-t,0],
        [0,-1,t],[0,1,t],[0,-1,-t],[0,1,-t],
        [t,0,-1],[t,0,1],[-t,0,-1],[-t,0,1],
      ];
      const verts = raw.map(v => v.map(x => x * s));
      const faces = [
        [0,11,5],[0,5,1],[0,1,7],[0,7,10],[0,10,11],
        [1,5,9],[5,11,4],[11,10,2],[10,7,6],[7,1,8],
        [3,9,4],[3,4,2],[3,2,6],[3,6,8],[3,8,9],
        [4,9,5],[2,4,11],[6,2,10],[8,6,7],[9,8,1],
      ];
      const edgeSet = new Set();
      const edges = [];
      for (const f of faces) {
        for (let i = 0; i < 3; i++) {
          const a = f[i], b = f[(i + 1) % 3];
          const key = Math.min(a, b) + '-' + Math.max(a, b);
          if (!edgeSet.has(key)) { edgeSet.add(key); edges.push([a, b]); }
        }
      }
      return { verts, edges };
    }

    function makeTorus(R, r, uSegs = 10, vSegs = 20) {
      const verts = [];
      const edges = [];
      for (let i = 0; i < uSegs; i++) {
        for (let j = 0; j < vSegs; j++) {
          const u = (i / uSegs) * Math.PI * 2;
          const v = (j / vSegs) * Math.PI * 2;
          verts.push([
            (R + r * Math.cos(u)) * Math.cos(v),
            (R + r * Math.cos(u)) * Math.sin(v),
            r * Math.sin(u),
          ]);
        }
      }
      for (let i = 0; i < uSegs; i++) {
        for (let j = 0; j < vSegs; j++) {
          const curr = i * vSegs + j;
          const nextU = ((i + 1) % uSegs) * vSegs + j;
          const nextV = i * vSegs + ((j + 1) % vSegs);
          edges.push([curr, nextU]);
          edges.push([curr, nextV]);
        }
      }
      return { verts, edges };
    }

    // --- Shape config ---
    // Tweak ox/oy to reposition, color to match your palette,
    // srx/sry/srz to change spin speed
    const shapes = [
      { geo: makeCube(55),          ox: -160, oy: -60,  oz: 0,   rx: 0, ry: 0, rz: 0, srx: 0.007,  sry: 0.011,  srz: 0.005,  color: '#378ADD', scale: 1   },
      { geo: makeIcosahedron(48),   ox: 0,    oy: 30,   oz: 20,  rx: 0, ry: 0, rz: 0, srx: 0.009,  sry: -0.007, srz: 0.006,  color: '#00d4aa', scale: 1   },
      { geo: makeOctahedron(42),    ox: 155,  oy: -50,  oz: -10, rx: 0, ry: 0, rz: 0, srx: -0.008, sry: 0.013,  srz: 0.004,  color: '#a78bfa', scale: 1   },
      { geo: makeCube(28),          ox: -80,  oy: 110,  oz: 30,  rx: 0, ry: 0, rz: 0, srx: 0.013,  sry: -0.009, srz: 0.007,  color: '#378ADD', scale: 0.7 },
      { geo: makeOctahedron(24),    ox: 100,  oy: 105,  oz: -20, rx: 0, ry: 0, rz: 0, srx: -0.011, sry: 0.008,  srz: 0.009,  color: '#00d4aa', scale: 0.7 },
      { geo: makeIcosahedron(20),   ox: -200, oy: 80,   oz: 10,  rx: 0, ry: 0, rz: 0, srx: 0.015,  sry: 0.006,  srz: -0.008, color: '#a78bfa', scale: 0.5 },
      { geo: makeCube(18),          ox: 210,  oy: 60,   oz: 0,   rx: 0, ry: 0, rz: 0, srx: -0.006, sry: 0.014,  srz: 0.01,   color: '#f4b710', scale: 0.5 },
      // { geo: makeTorus(38, 14),     ox: -30,  oy: -130, oz: 0,   rx: 0, ry: 0, rz: 0, srx: 0.008,  sry: 0.005,  srz: 0.011,  color: '#f0c040', scale: 0.9 },
      { geo: makeTorus(20, 8),      ox: 240,  oy: -90,  oz: 10,  rx: 0, ry: 0, rz: 0, srx: -0.01,  sry: 0.012,  srz: 0.006,  color: '#fd06068f', scale: 0.6 },

    ];

    const FOV = 340;
    const CX = W / 2, CY = H / 2  + H * 0.2;

    // --- Interaction state ---
    let rotX = 0.4, rotY = 0.3, velX = 0, velY = 0.004;
    let drag = false, lastMX = 0, lastMY = 0;
    let mouseX = W / 2, mouseY = H / 2;

    // Listeners on window so drag works even when cursor is over content above the canvas
    const onSelectStart = e => { if (drag) e.preventDefault(); };
    const onMouseDown = e => { drag = true; lastMX = e.clientX; lastMY = e.clientY; document.body.style.cursor = 'grabbing'; };
    const onMouseUp = () => { drag = false; document.body.style.cursor = ''; };
    const onMouseMove = e => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (drag) {
        velY += (e.clientX - lastMX) * DRAG_SENS;
        velX += (e.clientY - lastMY) * DRAG_SENS;
        lastMX = e.clientX; lastMY = e.clientY;
      }
    };

    const onTouchStart = e => {
      drag = true;
      lastMX = e.touches[0].clientX; lastMY = e.touches[0].clientY;
    };
    const onTouchEnd = () => { drag = false; };
    const onTouchMove = e => {
      const t = e.touches[0];
      mouseX = t.clientX;
      mouseY = t.clientY;
      if (drag) {
        velY += (t.clientX - lastMX) * DRAG_SENS;
        velX += (t.clientY - lastMY) * DRAG_SENS;
        lastMX = t.clientX; lastMY = t.clientY;
      }
    };

    window.addEventListener('selectstart', onSelectStart);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    // --- Draw helpers ---
    function hexToRgb(hex) {
      return [
        parseInt(hex.slice(1, 3), 16),
        parseInt(hex.slice(3, 5), 16),
        parseInt(hex.slice(5, 7), 16),
      ];
    }

    function drawShape(shape) {
      shape.rx += shape.srx;
      shape.ry += shape.sry;
      shape.rz += shape.srz;

      const projected = shape.geo.verts.map(v => {
        let p = [...v];
        p = rotateX(p, shape.rx);
        p = rotateY(p, shape.ry);
        p[0] += shape.ox; p[1] += shape.oy; p[2] += shape.oz;
        p = rotateX(p, rotX);
        p = rotateY(p, rotY);
        return project(p, FOV, CX, CY);
      });

      const [r, g, b] = hexToRgb(shape.color);

      for (const [a, bi] of shape.geo.edges) {
        const pa = projected[a], pb = projected[bi];
        const avgZ = (pa[2] + pb[2]) / 2;
        const depthAlpha = Math.min(Math.max((avgZ - 100) / 400, 0), 1);
        ctx.beginPath();
        ctx.moveTo(pa[0], pa[1]);
        ctx.lineTo(pb[0], pb[1]);
        ctx.strokeStyle = `rgba(${r},${g},${b},${0.15 + depthAlpha * 0.75})`;
        ctx.lineWidth = 0.8 * shape.scale;
        ctx.stroke();
      }

      for (const p of projected) {
        const depthAlpha = Math.min(Math.max((p[2] - 100) / 400, 0), 1);
        ctx.beginPath();
        ctx.arc(p[0], p[1], 1.2 * shape.scale, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${0.4 + depthAlpha * 0.6})`;
        ctx.fill();
      }
    }

    // --- Animation loop ---
    let animId;
    function draw() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#000000'; //060810
      ctx.fillRect(0, 0, W, H);

      if (!drag) {
        velY += (mouseX / window.innerWidth - 0.5) * DRAG_SENS;
        velX += (mouseY / window.innerHeight - 0.5) * DRAG_SENS;
      }
      velX *= 0.96; velY *= 0.96;
      rotX += velX; rotY += velY;

      for (const shape of shapes) drawShape(shape);

      animId = requestAnimationFrame(draw);
    }
    draw();

    // --- Cleanup ---
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('selectstart', onSelectStart);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        cursor: 'grab',
      }}
    />
  );
}
